import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import "../JaldiLab.css";
import {
  referenceHtml,
  referenceTootKit,
  h1style,
  h2style,
  h3style,
  h4style,
  getTextContentHeading,
  paraStyle,
  pointStyle,
  captionWithBorders,
  sectionStyle,
  subHeadingContent,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import visitingRoom from "../../../assets/Images/visitingRoom.jpg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import mulaqatRoom from "../../../assets/Images/VisitionRoom Images/MulaqatRoom.png";
import { ImageDesc63 } from "../../../utils/tmp/ImageDescription/VisitationRoomID";
import { ImageDesc57 } from "../../../utils/tmp/ImageDescription/VwdcID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

function VisitationRoom({ goToSection }) {
  const referencesRef = useRef(null);
  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content:
        <> ‘Restoration of Physical Mulakat Approved in All Jails in Odisha’ (<i>Pragativadi,</i> 18 February 2021) </>,
      link: "https://pragativadi.com/restoration-of-physical-mulakat-approved-in-all-jails-of-odisha/",
      description:
        "<https://pragativadi.com/restoration-of-physical-mulakat-approved-in-all-jails-of-odisha/> [“Pragativadi”] ",
    },
    {
      content: <>Infrastructure’ <i>(Model Jails, Chandigarh Prison Department)</i> </>,
      link: "https://chdmodeljail.gov.in/pages/infrastructure#",
      description: "<https://chdmodeljail.gov.in/pages/infrastructure#>",
    },
  ];

  const ObjectivesArea = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Ensure a
        <span style={{ color: "#DC002D" }}> hassle-free environment</span> for
        the Undertrials and visitors/lawyers to meet and communicate with each
        other;
      </>,
      <>
        Plan facilities to ensure
        <span style={{ color: "#DC002D" }}>
          {" "}
          ease of access and communication for diverse users
        </span>
        ;{" "}
      </>,
      <>
        Ensure
        <span style={{ color: "#DC002D" }}>
          {" "}
          accessible, safe and secure rooms
        </span>{" "}
        for all users and{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          prevent incidents of confrontation or attack
        </span>
        ; and
      </>,
      <>
        Design a
        <span style={{ color: "#DC002D" }}>
          {" "}
          non-intimidating and humane security barrier
        </span>{" "}
        between the Accused Persons and their visitors/lawyers.
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const userObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Accused/Under-trials",
      "Visitors (Family Members/Friends)",
      "Lawyers",
      "Police Inspectors and Security Staff",
    ],
    isOrdered: false,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The Visiting Room in a Court Complex must be directly adjacent to the lock-up facilities and the Police Inspectors’ Room.",
      <>
        A waiting area must be located near the Visiting Room for the use of the
        Advocates/family members waiting to speak to the Accused Persons.
        {reference(referenceData[1], 2)}
      </>,
      "The Visiting Room must have access to the secure corridors, elevators and stairs between the lock-up and the waiting area, and additionally must be accessible for Advocates and family members from other public-facing parts of the Court.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const DesignConsiderations = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        "The Visiting Room must be equipped with facilities permitting visual access and verbal communication (direct or via a telephone) between the Accused Persons and their visitors.",

        "Adequate audio and video facilities must also be provided, to facilitate clear remote communication with the family members or visitors.",

        "The partition between the parties must be transparent and made of secure and sturdy material, to ensure that the Accused Persons and their visitors are able to see each other clearly. ",

        "Strict security measures such as barriers, closed circuit cameras, etc., must be adopted to monitor the room and the activities of the users. A computerised set-up must be provided for monitoring and recording the meetings. The surveillance system must be programmed to trigger alarms especially for unattended or leftover baggage, before allowing exit of the visiting persons.",

        "Accessible, comfortable and ergonomic seating must be provided for the visitors as well as the Accused.",
      ],
    },
    {
      heading: "Meeting Area",
      items: [
        "Small, segregated meeting rooms must be provided in a part of the Visiting Room to facilitate meetings between the Accused and their Advocates. ",

        "The design of the meeting rooms should be such that necessary monitoring of the Accused's movement can take place from the main Visiting Room, minimising interference risks.",

        <>
          The meeting rooms must be acoustically treated to ensure privacy and
          confidentiality from the other users of the space. For further details
          on acoustics, please refer to{" "}
          <span
            style={sectionStyle}
            onClick={() => goToSection("annexes", "HeadingAnnexure5")}
          >
            Annexure 5.
          </span>
        </>,

        "Discreet alarms or panic buttons must be added in the meeting area for immediate assistance during emergencies.",

        "Accessible display screens, with audible announcements, regarding case information and progress must be provided.",

        "The meeting room must be built to ensure that the Accused Persons are able to have private conversations with their lawyers. However, a computerised setup should be provided for monitoring and recording the meetings (for future use, if necessary).",
      ],
    },
  ];

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 style={h3style}>
          <span style={{ fontSize: "20px" }}>2.7. </span>
          Facilities for Accused Persons
        </h3>
        <h4 id="headingVistingRoom" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.7.</span>B. Visiting Room for the
          Accused
        </h4>
        <p style={paraStyle}>
          The Visiting Room for the Accused, also known as Mulakat Room in some
          jurisdictions,{reference(referenceData[0], 1)} is a computerised room
          provided for facilitating meetings and communication between the
          Accused/under-trials while at the Court, and their family members,
          friends or lawyers, as required.
        </p>
        <CaptionWithBorders1
          image={[visitingRoom]}
          caption={
            "Image 63: Example of a Visiting Room or Mulakat Room in Jails in Odisha"
          }
          creditLabel={"Source"}
          imageCredit={"Pragativadi"}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc63}
        />
        {getTextContentHeading(ObjectivesArea)}
        {getTextContentHeading(userObject)}
        {getTextContentHeading(adjacenciesObject)}
        {subHeadingContent(DesignConsiderations)}
        <CaptionWithBorders1
          image={[mulaqatRoom]}
          isZoom={true}
          caption={"Figure 57: Typical floor plan of a Visiting Room"}
          zoomImgStyle={{ height: "650px" }}
          imageDescription={ImageDesc57}
        />
        <h2 ref={referencesRef} id="reference" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
}

export default VisitationRoom;
